/**
 * Step 1 of the funnel (CLAUDE.md's Applications section) — unauthenticated,
 * free, no record created. Shows the firm's published criteria and asks a
 * few self-reported questions, so someone who won't qualify learns it before
 * paying an application fee or handing over an SSN.
 *
 * `screening_criteria` starts empty (see the module header in
 * modules/applications/service.ts) — no firm has published anything yet in
 * this environment, so the empty-state copy below is the expected, common
 * case today, not an error path.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api.js';

interface ScreeningCriterionDto {
  id: string;
  code: string;
  description: string;
}

export function PreQualification() {
  const [criteria, setCriteria] = useState<ScreeningCriterionDto[] | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await api<{ data: ScreeningCriterionDto[] }>(
          '/pre-qualification-criteria',
        );
        if (!cancelled) setCriteria(response.data);
      } catch {
        if (!cancelled) setError('Could not load the current criteria. You can still apply below.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section>
      <div className="apply-hero">
        <div className="apply-arch" />
        <p className="apply__eyebrow">Rental application</p>
        <h1>Find your next front door.</h1>
        <p className="apply__lede">
          Take a minute to see what we're looking for before you apply — it's free, and it means no
          surprises later.
        </p>
      </div>

      <div className="card stack">
        {error ? <p className="alert alert--error">{error}</p> : null}

        {criteria === null && !error ? <p className="muted">Loading…</p> : null}

        {criteria !== null && criteria.length === 0 ? (
          <p className="muted">
            We haven't published specific screening criteria yet. You're welcome to apply, and our
            team will follow up with any questions.
          </p>
        ) : null}

        {criteria !== null && criteria.length > 0 ? (
          <>
            <h2>What we look for</h2>
            <div className="stack">
              {criteria.map((criterion) => (
                <label key={criterion.id} className="apply-check-row">
                  <input
                    type="checkbox"
                    checked={checked[criterion.id] ?? false}
                    onChange={(event) =>
                      setChecked((prev) => ({ ...prev, [criterion.id]: event.target.checked }))
                    }
                  />
                  <span>{criterion.description}</span>
                </label>
              ))}
            </div>
            <p className="muted">
              Not sure you meet everything above? Apply anyway — this is a self-check, not a filter.
            </p>
          </>
        ) : null}
      </div>

      <div className="row" style={{ marginTop: '1.75rem' }}>
        <Link to="/apply/sign-up" className="button">
          Start an application
        </Link>
      </div>
    </section>
  );
}
